/**
 * 조망 카드 값의 색 강조 규칙. 제품 결정(2026-08-19)이고 FE 가 소유한다.
 *
 * `episodeDiagnosticValues.ts` 는 값과 데이터셋 안 위치만 낸다. 심각도는 만들지 않는다.
 * 행동 분산과 상태 유사도는 선호 방향이 반대라서(근거 논문 Eq. 8) 좋고 나쁨을 판정할 수 없다.
 * 그래도 "어디부터 볼까"에는 답이 필요하다. 그래서 판정 대신 **분포 양끝을 같은 무게로
 * 강조**한다. attention(붉은색)은 잘못이라는 뜻이 아니다. 먼저 확인할 곳이라는 뜻이다.
 *
 * Owns:
 * - 백분위 꼬리 경계와 밴드 폭 절대 임계, 그 구간 판정.
 *
 * Does not own:
 * - 백분위·밴드 폭 계산(`episodeDiagnosticValues.ts`·`datasetOverview.ts`).
 * - 색 토큰.
 * - 기준 밖 비율의 심각도(`bandDeviation.ts`). 그 축은 통로라는 절대 기준이 있어서 규칙이
 *   다르다.
 */

/** 값 강조. calm=무난(녹), buffer=완충(회), attention=먼저 볼 곳(적). */
export type OverviewEmphasis = "calm" | "buffer" | "attention";

// 백분위 꼬리 경계(경계 포함). 양끝 10% 는 강조, 20% 까지는 완충, 가운데 60% 는 무난이다.
export const RANK_ATTENTION_TAIL = 0.1;
export const RANK_BUFFER_TAIL = 0.2;

/**
 * 데이터셋 내 백분위(0~1)를 강조로 바꾼다. 가까운 쪽 끝까지의 거리를 재서 양끝을 같은
 * 무게로 본다. 이 방식은 어느 쪽이 좋은지 정하지 않는다는 결정과 맞는다.
 * 백분위가 없으면(표본 없음) null 이다.
 */
export function rankEmphasis(rank: number | null): OverviewEmphasis | null {
  if (rank === null) {
    return null;
  }
  const tail = Math.min(rank, 1 - rank);
  if (tail <= RANK_ATTENTION_TAIL) {
    return "attention";
  }
  if (tail <= RANK_BUFFER_TAIL) {
    return "buffer";
  }
  return "calm";
}

// 밴드 폭 절대 임계. 데이터셋에 값이 하나라서 비교할 표본이 없다. 그래서 백분위 대신
// 절대값으로 구분한다. 이탈률과 같이 FE 가 소유하는 상수다. 실측 근거가 늘면 조정한다.
export const BAND_WIDTH_CALM_MAX = 0.25;
export const BAND_WIDTH_ATTENTION_MIN = 0.5;

/**
 * 밴드 폭 비율(0~1)을 강조로 바꾼다.
 * - 폭이 좁으면 시연이 모여 있어 기준이 엄격하다(calm).
 * - 폭이 값 범위의 절반을 넘으면 기준이 느슨해서 이탈 표시를 믿기 어렵다(attention).
 */
export function bandWidthEmphasis(ratio: number): OverviewEmphasis {
  if (ratio >= BAND_WIDTH_ATTENTION_MIN) {
    return "attention";
  }
  if (ratio < BAND_WIDTH_CALM_MAX) {
    return "calm";
  }
  return "buffer";
}
